/* Product page V2 — sections/main-product-v2.liquid */

if (!customElements.get('product-gallery-v2')) {
  // Infinite carousel (all screen sizes, 3+ photos). At least LOOP_CLONES copies of the last photos are placed
  // before the real ones and of the first photos after them; when scrolling settles on a copy, the track jumps
  // (no animation) to the matching real photo. Copies carry no data-media-id, so the theme's variant gallery swap
  // (ProductInfo.morphGalleryMedia) drops them, and countMediaGallery() rebuilds them.
  // 1-2 photos: plain swipe, the arrows wrap around from last to first.
  const LOOP_CLONES = 3;
  // Photo width changes at the desktop breakpoint, and with it the number of copies needed: rebuilt when crossing it.
  const LOOP_QUERY = '(min-width: 1024px)';

  class ProductGalleryV2 extends HTMLElement {
    connectedCallback() {
      this.onScroll = this.onScroll.bind(this);
      this.onScrollEnd = this.onScrollEnd.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onMediaChange = () => this.setupLoop();

      // Bound on the element (scroll via capture): ProductInfo may replace the track's HTML on variant change.
      this.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      this.addEventListener('scrollend', this.onScrollEnd, { capture: true });
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      this.loopQuery = window.matchMedia(LOOP_QUERY);
      this.loopQuery.addEventListener('change', this.onMediaChange);
      this.setupLoop();
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.onScroll, { capture: true });
      this.removeEventListener('scrollend', this.onScrollEnd, { capture: true });
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
      this.loopQuery?.removeEventListener('change', this.onMediaChange);
      clearTimeout(this.settleTimer);
      this._photoswipe?.destroy?.();
      this._photoswipe = null;
    }

    get track() {
      return this.querySelector('.pv2-gallery__track');
    }

    // Real photos only (copies have no data-media-id).
    get items() {
      return Array.from(this.track.querySelectorAll('[data-media-id]'));
    }

    get looping() {
      return this.classList.contains('is-loop');
    }

    // ProductInfo calls this after it swaps the gallery for another variant's photos.
    countMediaGallery() {
      this.setupLoop();
    }

    setupLoop() {
      const track = this.track;
      track.querySelectorAll('[data-loop-clone]').forEach((clone) => clone.remove());

      const items = this.items;
      const loop = items.length > 2;
      this.classList.toggle('is-loop', loop);

      if (loop) {
        // Enough copies on each side to fill the visible width (small photos on wide, short screens need more).
        const step = items[1].offsetLeft - items[0].offsetLeft;
        const count = Math.max(LOOP_CLONES, Math.ceil(track.clientWidth / step) + 1);
        const total = items.length;
        const before = Array.from({ length: count }, (_, i) => items[(((total - count + i) % total) + total) % total]);
        const after = Array.from({ length: count }, (_, i) => items[i % total]);
        const makeClone = (item) => {
          const clone = item.cloneNode(true);
          clone.removeAttribute('data-media-id');
          clone.setAttribute('data-loop-clone', '');
          clone.setAttribute('data-loop-index', String(items.indexOf(item)));
          clone.setAttribute('aria-hidden', 'true');
          clone.inert = true;
          clone.querySelectorAll('img').forEach((img) => {
            img.removeAttribute('fetchpriority');
            img.loading = 'lazy';
          });
          return clone;
        };
        before.forEach((item) => track.insertBefore(makeClone(item), items[0]));
        after.forEach((item) => track.append(makeClone(item)));
      }

      this.jumpTo(0);
      this.update();
    }

    get step() {
      const children = Array.from(this.track.children);
      if (!children[0]) return 0;
      return children[1] ? children[1].offsetLeft - children[0].offsetLeft : children[0].offsetWidth;
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    // scrollLeft that puts real photo `index` at the left edge.
    positionOf(index) {
      const first = this.track.firstElementChild;
      const item = this.items[index];
      return item && first ? item.offsetLeft - first.offsetLeft : 0;
    }

    // Index of the real photo at the left edge (copies map back to their real photo).
    get currentIndex() {
      const total = this.items.length;
      const step = this.step;
      if (!step || !total) return 0;
      const raw = Math.round((this.track.scrollLeft - this.positionOf(0)) / step);
      return this.looping ? ((raw % total) + total) % total : Math.max(0, Math.min(raw, total - 1));
    }

    jumpTo(index) {
      const track = this.track;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = Math.min(this.positionOf(index), this.maxScroll);
      track.style.scrollBehavior = '';
    }

    // On a copy: shift by exactly one full set of photos, so the same image stays at the same spot (invisible).
    normalize() {
      if (!this.looping) return;
      const step = this.step;
      const total = this.items.length;
      if (!step) return;
      const offset = this.track.scrollLeft - this.positionOf(0);
      let shift = 0;
      if (offset < -step / 2) shift = total * step;
      else if (offset > (total - 0.5) * step) shift = -total * step;
      if (!shift) return;

      const track = this.track;
      track.style.scrollBehavior = 'auto';
      track.style.scrollSnapType = 'none';
      track.scrollLeft += shift;
      track.style.scrollSnapType = '';
      track.style.scrollBehavior = '';
    }

    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
      // Fallback for browsers without the scrollend event (Safari).
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.onScrollEnd(), 160);
    }

    onScrollEnd() {
      clearTimeout(this.settleTimer);
      this.normalize();
      this.update();
    }

    update() {
      const total = this.items.length;
      let current = this.currentIndex + 1;
      // No loop (1-2 photos): the last photo may not reach the left edge, so the end of the track counts as the last one.
      if (!this.looping && total > 1 && this.track.scrollLeft >= this.maxScroll - 2) current = total;

      this.classList.toggle('is-single', total <= 1);
      this.querySelector('[data-gallery-total]').textContent = total;
      this.querySelector('[data-gallery-current]').textContent = Math.max(Math.min(current, total), 1);
      // Arrows never block: the loop continues, and without it they wrap around.
      this.querySelector('[data-gallery-prev]').disabled = total <= 1;
      this.querySelector('[data-gallery-next]').disabled = total <= 1;
    }

    go(direction) {
      const step = this.step;
      const total = this.items.length;
      if (!step || total <= 1) return;
      const behavior = theme?.config?.motionReduced ? 'instant' : 'smooth';

      if (this.looping) {
        // Settle any pending copy position first so quick repeated clicks never run out of copies.
        this.normalize();
        this.track.scrollTo({ left: this.positionOf(this.currentIndex) + direction * step, behavior });
        return;
      }

      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= this.maxScroll - 2;
      if (direction > 0 && atEnd) return this.track.scrollTo({ left: 0, behavior });
      if (direction < 0 && atStart) return this.track.scrollTo({ left: this.maxScroll, behavior });
      const target = Math.max(0, Math.min((this.currentIndex + direction) * step, this.maxScroll));
      this.track.scrollTo({ left: target, behavior });
    }

    onClick(event) {
      if (event.target.closest('[data-gallery-prev]')) return this.go(-1);
      if (event.target.closest('[data-gallery-next]')) return this.go(1);

      const zoom = event.target.closest('[data-gallery-zoom]');
      if (!zoom) return;
      const slide = zoom.closest('.pv2-gallery__item');
      const index = slide.hasAttribute('data-loop-index') ? Number(slide.dataset.loopIndex) : this.items.indexOf(slide);
      this.openZoom(index);
    }

    onKeydown(event) {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      }
    }

    get photoswipe() {
      if (this._photoswipe) return this._photoswipe;
      if (typeof PhotoSwipeLightbox === 'undefined') return null;

      // Same options as the theme's MediaGallery lightbox.
      const lightbox = new PhotoSwipeLightbox({
        arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
        arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
        closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
        // Outlined like the other buttons (PhotoSwipe's default is a filled magnifier; inline fill beats the theme's
        // .pswp__icn fill rule, which only the closed paths of a circle show). The vertical bar hides
        // once zoomed in (theme CSS), so + becomes −.
        zoomSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" style="fill: none" viewBox="0 0 30 30"><circle cx="13.5" cy="13.5" r="7.5"/><path d="m19 19 4.5 4.5M10 13.5h7"/><path class="pswp__zoom-icn-bar-v" d="M13.5 10v7"/></svg>',
        bgOpacity: 1,
        pswpModule: () => import(theme.settings.pswpModule),
        arrowPrevTitle: theme.strings.previous,
        arrowNextTitle: theme.strings.next,
        closeTitle: theme.strings.close,
        allowPanToNext: false,
        allowMouseDrag: true,
        wheelToZoom: false,
        returnFocus: true
      });

      lightbox.addFilter('thumbEl', (_thumbEl, data) => data.thumbnailElement);

      // Keep the gallery in step with the photo being viewed, so closing returns to it.
      lightbox.on('change', () => {
        const item = this.zoomItems?.[lightbox.pswp.currIndex];
        if (item) this.jumpTo(this.items.indexOf(item));
      });

      lightbox.init();
      return (this._photoswipe = lightbox);
    }

    openZoom(index) {
      const lightbox = this.photoswipe;
      if (!lightbox) return;

      this.zoomItems = this.items.filter((item) => item.getAttribute('data-media-type') === 'image');
      const dataSource = this.zoomItems.map((item) => {
        const image = item.querySelector('img');
        return {
          thumbnailElement: image,
          src: image.getAttribute('data-zoom-src') || image.currentSrc || image.src,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('data-zoom-width'), 10) || image.naturalWidth,
          height: parseInt(image.getAttribute('data-zoom-height'), 10) || image.naturalHeight,
          alt: image.alt,
          thumbCropped: true
        };
      });

      const zoomIndex = Math.max(0, this.zoomItems.indexOf(this.items[index]));
      lightbox.loadAndOpen(zoomIndex, dataSource);
    }
  }

  customElements.define('product-gallery-v2', ProductGalleryV2);
}

// Theme settings > Compatibility Warnings, rendered by main-product-v2 as #Pv2Compat-{section id}.
// An add-on is excluded for the selected desk variant when a rule has the add-on as source and lists the
// desk product or variant, or has the desk as source and lists the add-on product.
function pv2CompatibilityFor(productInfo) {
  const script = productInfo && document.getElementById(`Pv2Compat-${productInfo.dataset.sectionId}`);
  let data = { productId: 0, rules: [] };
  try {
    if (script) data = JSON.parse(script.textContent);
  } catch (error) {
    console.warn('[product-v2] Invalid compatibility rules', error);
  }

  return (addonProductId, deskVariantId) => data.rules.some((rule) => (
    (rule.source === addonProductId && (rule.products.includes(data.productId) || rule.variants.includes(deskVariantId))) ||
    (rule.source === data.productId && rule.products.includes(addonProductId))
  ));
}

// Add-on variant that matches the desk selection best: most option values in common with the desk's
// (e.g. desk Maple / 30" x 60" / White -> drawer "White / Maple", stand "Maple"), available ones only.
// Returns null when nothing matches, so the add-on keeps its current/default variant.
function pv2BestVariant(variants, deskValues, currentId) {
  const desk = new Set((deskValues || []).map((value) => String(value).trim().toLowerCase()));
  if (!desk.size) return null;

  let best = null;
  let bestScore = 0;
  variants.forEach((variant) => {
    if (!variant.available) return;
    const score = variant.options.filter((value) => desk.has(String(value).trim().toLowerCase())).length;
    if (score > bestScore || (score === bestScore && score > 0 && String(variant.id) === String(currentId))) {
      best = variant;
      bestScore = score;
    }
  });
  return best;
}

if (!customElements.get('product-addon-v2')) {
  // Add-on category in the panel. data-mode="dropdown": inline list with Add toggles.
  // data-mode="drawer": opens the category drawer, shows the confirmed products, "Remove" clears them.
  class ProductAddonV2 extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'dropdown';
      this.selections = [];

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      if (this.mode === 'drawer') {
        // The drawer moves itself to <body> while open, so keep a direct reference.
        this.drawer = document.getElementById(this.dataset.drawerId);
        this.picker = this.drawer?.querySelector('product-addon-picker-v2');
        if (this.picker) this.picker.addon = this;
      }

      // Re-check exclusions whenever the desk variant changes (ProductInfo fires variant:change on the form).
      const productInfo = this.closest('product-info');
      this.isIncompatible = pv2CompatibilityFor(productInfo);
      this.form = productInfo && document.forms[productInfo.getAttribute('form')];
      this.onVariantChange = (event) => this.onDeskVariant(event.detail?.variant, true);
      this.form?.addEventListener('variant:change', this.onVariantChange);

      const selected = productInfo?.querySelector('[data-selected-variant]');
      let initialVariant = null;
      try {
        initialVariant = selected ? JSON.parse(selected.textContent) : null;
      } catch (error) { /* keep null */ }
      this.onDeskVariant(initialVariant, false);
      this.render();
    }

    // Desk variant changed: drop add-ons it excludes, and line up the others with its wood/colour.
    onDeskVariant(variant, notify) {
      if (!variant) return;
      this.deskValues = variant.options || [];
      this.applyCompatibility(variant.id, notify);
      if (this.mode === 'dropdown') this.matchDropdownToDesk();
    }

    matchDropdownToDesk() {
      this.querySelectorAll('[data-addon-variant]').forEach((select) => {
        const checkbox = select.closest('.pv2-addon__item').querySelector('[data-addon-checkbox]');
        if (checkbox.checked) return; // keep what the customer already added
        const variants = Array.from(select.options).map((option) => ({
          id: option.value,
          options: option.textContent.trim().split(' / '),
          available: !option.disabled
        }));
        const best = pv2BestVariant(variants, this.deskValues, select.value);
        if (best && best.id !== select.value) {
          select.value = best.id;
          this.updateFromSelect(select);
        }
      });
      this.render();
    }

    updateFromSelect(select) {
      const item = select.closest('.pv2-addon__item');
      const option = select.selectedOptions[0];
      const checkbox = item.querySelector('[data-addon-checkbox]');
      checkbox.value = option.value;
      if (option.dataset.thumb) checkbox.dataset.thumb = option.dataset.thumb;
      item.querySelector('[data-addon-price]').textContent = option.dataset.price;
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('variant:change', this.onVariantChange);
    }

    applyCompatibility(deskVariantId, notify) {
      if (!deskVariantId) return;
      const excluded = (productId) => this.isIncompatible(Number(productId), Number(deskVariantId));
      this.excluded = excluded;
      const removed = [];

      if (this.mode === 'drawer') {
        this.picker?.setIncompatible?.(excluded);
        const kept = this.selections.filter((selection) => {
          if (!excluded(selection.productId)) return true;
          removed.push(selection.title);
          return false;
        });
        if (removed.length) this.setSelections(kept);
      } else {
        this.querySelectorAll('[data-addon-product-id]').forEach((item) => {
          const incompatible = excluded(item.dataset.addonProductId);
          const checkbox = item.querySelector('[data-addon-checkbox]');
          if (incompatible && checkbox.checked) {
            checkbox.checked = false;
            removed.push(checkbox.dataset.title);
          }
          checkbox.disabled = incompatible || item.dataset.available !== 'true';
          item.classList.toggle('is-incompatible', incompatible);
          item.querySelector('[data-addon-incompatible-note]').hidden = !incompatible;
        });
        this.render();
      }

      const note = this.querySelector('[data-addon-note]');
      if (!notify || !note) return;
      note.textContent = removed.length ? (this.dataset.removedNote || '').replace('[product]', removed.join(', ')) : '';
      note.hidden = removed.length === 0;
    }

    onClick(event) {
      if (event.target.closest('[data-addon-remove]')) {
        this.clear();
        return;
      }

      // Drawer mode: the theme's <drawer-element> opens itself from aria-controls; start from what is confirmed.
      if (this.mode === 'drawer' && event.target.closest('.pv2-addon__toggle')) {
        if (this.excluded) this.picker?.setIncompatible?.(this.excluded);
        this.picker?.reset?.(this.selections, this.deskValues);
        return;
      }

      if (this.mode === 'dropdown' && event.target.closest('[data-addon-dropdown]')) {
        const toggle = this.querySelector('[data-addon-dropdown]');
        const list = this.querySelector('.pv2-addon__list');
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        list.hidden = !open;
        if (open) list.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    onChange(event) {
      if (this.mode !== 'dropdown') return;

      const select = event.target.closest('[data-addon-variant]');
      if (select) this.updateFromSelect(select);
      this.render();
    }

    setSelections(selections) {
      this.selections = selections;
      this.render();
    }

    clear() {
      if (this.mode === 'drawer') {
        this.setSelections([]);
      } else {
        this.querySelectorAll('[data-addon-checkbox]:checked').forEach((input) => { input.checked = false; });
        this.render();
      }
    }

    render() {
      return this.mode === 'drawer' ? this.renderDrawerMode() : this.renderDropdownMode();
    }

    renderDropdownMode() {
      const selected = Array.from(this.querySelectorAll('[data-addon-checkbox]:checked'));
      const summary = this.querySelector('[data-addon-summary]');
      const thumbs = this.querySelector('[data-addon-thumbs]');

      summary.textContent = selected.length
        ? summary.dataset.selectedLabel.replace('[count]', selected.length)
        : summary.dataset.emptyLabel;

      thumbs.replaceChildren(...selected.filter((input) => input.dataset.thumb).slice(0, 4).map((input) => {
        const img = document.createElement('img');
        img.src = input.dataset.thumb;
        img.alt = '';
        img.width = 36;
        img.height = 36;
        return img;
      }));
      thumbs.hidden = thumbs.childElementCount === 0;
      this.querySelector('[data-addon-remove]').hidden = selected.length === 0;
    }

    renderDrawerMode() {
      const selections = this.selections;
      const list = this.querySelector('[data-addon-selected]');

      // Checked "bundles" inputs inside <product-info form> are what ProductForm adds to the cart.
      this.querySelector('[data-addon-inputs]').replaceChildren(...selections.map((selection) => {
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'bundles';
        input.value = selection.variantId;
        input.checked = true;
        input.dataset.min = '1';
        // Shows in the order which lines came from the add-ons (theme.js ProductForm.prepareFormData)
        input.dataset.properties = JSON.stringify({ _upsell_add_ons: 'true' });
        return input;
      }));

      list.replaceChildren(...selections.map((selection) => {
        const row = document.createElement('span');
        row.className = 'pv2-addon__row';

        const media = document.createElement('span');
        media.className = 'pv2-addon__row-media';
        if (selection.thumb) {
          const img = document.createElement('img');
          img.src = selection.thumb;
          img.alt = '';
          img.width = 48;
          img.height = 48;
          media.append(img);
        }

        const text = document.createElement('span');
        text.className = 'pv2-addon__row-text';
        const name = document.createElement('span');
        name.className = 'pv2-addon__row-name';
        name.textContent = selection.title;
        text.append(name);
        if (selection.variantTitle) {
          const variant = document.createElement('span');
          variant.className = 'pv2-addon__row-variant';
          variant.textContent = selection.variantTitle;
          text.append(variant);
        }

        const price = document.createElement('span');
        price.className = 'pv2-addon__row-price';
        price.textContent = selection.price;

        row.append(media, text, price);
        return row;
      }));

      list.hidden = selections.length === 0;
      this.querySelector('[data-addon-empty]').hidden = selections.length > 0;
      this.querySelector('[data-addon-remove]').hidden = selections.length === 0;
    }
  }

  customElements.define('product-addon-v2', ProductAddonV2);
}

if (!customElements.get('product-addon-picker-v2')) {
  // Inside an add-on drawer: option choice, price/photo per variant, Add toggles, Confirm.
  // Choices are a draft until "Confirm Selection"; reset() restores the confirmed state on every open.
  class ProductAddonPickerV2 extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    get drawer() {
      return this.closest('drawer-element');
    }

    get items() {
      return Array.from(this.querySelectorAll('[data-addon-product]'));
    }

    variantsOf(item) {
      if (!item._variants) item._variants = JSON.parse(item.querySelector('[data-addon-variants]').textContent);
      return item._variants;
    }

    currentVariant(item) {
      const variants = this.variantsOf(item);
      const fieldsets = Array.from(item.querySelectorAll('[data-addon-option]'));
      if (!fieldsets.length) return variants[0];

      const chosen = fieldsets.map((fieldset) => fieldset.querySelector('input:checked')?.value);
      return variants.find((variant) => variant.options.every((value, index) => value === chosen[index])) || null;
    }

    onChange(event) {
      const item = event.target.closest('[data-addon-product]');
      if (!item) return;

      const fieldset = event.target.closest('[data-addon-option]');
      const label = fieldset?.querySelector('[data-addon-option-value]');
      if (label) label.textContent = event.target.value;
      this.updateItem(item);
    }

    onClick(event) {
      const add = event.target.closest('[data-addon-add]');
      if (add) {
        add.setAttribute('aria-pressed', String(add.getAttribute('aria-pressed') !== 'true'));
        return;
      }

      if (event.target.closest('[data-addon-confirm]')) this.confirm();
    }

    updateItem(item) {
      const variant = this.currentVariant(item);
      const add = item.querySelector('[data-addon-add]');
      const image = item.querySelector('[data-addon-image]');

      if (variant) {
        item.querySelector('[data-addon-price]').textContent = variant.price;
        if (image && variant.image && image.dataset.variantImage !== variant.image) {
          image.dataset.variantImage = variant.image;
          image.removeAttribute('srcset');
          image.src = variant.image;
        }
      }

      const incompatible = item.dataset.incompatible === 'true';
      item.classList.toggle('is-incompatible', incompatible);
      item.querySelector('[data-addon-incompatible-note]').hidden = !incompatible;

      const available = Boolean(variant && variant.available) && !incompatible;
      add.disabled = !available;
      if (!available) add.setAttribute('aria-pressed', 'false');
    }

    selectOptions(item, values) {
      item.querySelectorAll('[data-addon-option]').forEach((fieldset, index) => {
        const value = values[index];
        const input = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.value === value);
        if (input) input.checked = true;
        const label = fieldset.querySelector('[data-addon-option-value]');
        if (label && value) label.textContent = value;
      });
    }

    setIncompatible(excluded) {
      this.items.forEach((item) => {
        item.dataset.incompatible = String(excluded(item.dataset.addonProduct));
        this.updateItem(item);
      });
    }

    // Confirmed add-ons show their confirmed variant; the others follow the desk's wood/colour when possible.
    reset(selections, deskValues) {
      this.items.forEach((item) => {
        const selection = selections.find((entry) => String(entry.productId) === item.dataset.addonProduct);
        const variants = this.variantsOf(item);
        const variant = selection
          ? variants.find((entry) => String(entry.id) === String(selection.variantId))
          : pv2BestVariant(variants, deskValues, this.currentVariant(item)?.id);
        if (variant) this.selectOptions(item, variant.options);
        this.updateItem(item);
        const add = item.querySelector('[data-addon-add]');
        if (!add.disabled) add.setAttribute('aria-pressed', String(Boolean(selection)));
      });
    }

    confirm() {
      const selections = this.items.flatMap((item) => {
        if (item.querySelector('[data-addon-add]').getAttribute('aria-pressed') !== 'true') return [];
        const variant = this.currentVariant(item);
        if (!variant || !variant.available) return [];
        return [{
          productId: item.dataset.addonProduct,
          variantId: variant.id,
          title: item.dataset.title,
          variantTitle: variant.title,
          price: variant.price,
          thumb: variant.thumb
        }];
      });

      this.addon?.setSelections(selections);
      this.drawer?.hide();
    }
  }

  customElements.define('product-addon-picker-v2', ProductAddonPickerV2);
}


// Financing line: "Learn more" opens Affirm's modal for the selected variant's price, and the line
// (monthly amount, hidden under the minimum price) is swapped from ProductInfo's re-render on variant change.
(function () {
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-affirm-learn-more]');
    if (!link) return;

    const amount = Number(link.closest('[data-amount]')?.dataset.amount) || undefined;
    if (window.affirm?.ui?.openModal) {
      window.affirm.ui.ready(() => window.affirm.ui.openModal({ amount, pageType: 'product' }));
      return;
    }
    // Fallback: the Affirm app block's own "Learn more" link, if its script rendered one.
    document.querySelector('.pv2-app .affirm-as-low-as a, .pv2-app .affirm-modal-trigger')?.click();
  });

  const pubsub = window.theme?.pubsub;
  if (!pubsub?.PUB_SUB_EVENTS?.variantChange) return;
  pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
    if (!data?.parsedHTML) return;
    const id = `Pv2Financing-${data.sectionId}`;
    const current = document.getElementById(id);
    const updated = data.parsedHTML.getElementById(id);
    if (current && updated) current.replaceWith(document.importNode(updated, true));
  });
})();

// Panel width fit (window.pv2Fit, defined inline by snippets/product-v2-fit.liquid right after the panel so the
// first paint already has the right width): fit again on resize, variant change and once the web fonts are loaded.
(function () {
  function fit(root) {
    if (window.pv2Fit) window.pv2Fit(root);
  }

  function fitAll(scope = document) {
    scope.querySelectorAll('.main-product-v2').forEach((root) => fit(root));
  }

  let timer;
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => fitAll(), 120);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => fitAll());
  else fitAll();
  document.fonts?.ready?.then(() => fitAll());
  window.addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', (event) => fit(event.target));
  // The variant picker is re-rendered on variant change (its classes come back from the server).
  const pubsub = window.theme?.pubsub;
  if (pubsub?.PUB_SUB_EVENTS?.variantChange) pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, schedule);
})();

// Business quote block: the "Request a quote" link opens the form dialog; close button / backdrop click
// close it. After the form is sent, Shopify reloads the page and the dialog reopens on its message.
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-pv2-b2b-open]');
  if (opener) {
    document.getElementById(opener.getAttribute('data-pv2-b2b-open'))?.showModal();
    return;
  }

  const dialog = event.target.closest('.pv2-b2b-dialog');
  if (!dialog) return;
  // A click on the backdrop lands on the <dialog> itself, outside its content box
  const rect = dialog.getBoundingClientRect();
  const outside = event.target === dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom);
  if (outside || event.target.closest('[data-pv2-b2b-close]')) dialog.close();
});

document.querySelectorAll('.pv2-b2b-dialog[data-pv2-b2b-autoopen]').forEach((dialog) => dialog.showModal());

// Business quote model field without a "Default model": preselect the option closest to the product's title
// (word overlap, works on the translated texts too: "Leva Solid Wood Standing Desk", "Bureau ajustable Leva en
// bois massif"). Below the threshold nothing is preselected rather than a wrong model (e.g. Bamboo).
function pv2B2bBestModel(title, options, threshold = 0.65) {
  const words = (text) => new Set(String(text).toLowerCase().normalize('NFC').split(/[^\p{L}\p{N}]+/u).filter(Boolean));
  const target = words(title);
  let best = null;
  let bestScore = 0;
  options.forEach((option) => {
    const set = words(option.textContent);
    const common = [...set].filter((word) => target.has(word)).length;
    const score = common / new Set([...set, ...target]).size;
    if (score > bestScore) {
      best = option;
      bestScore = score;
    }
  });
  return bestScore >= threshold ? best : null;
}

document.querySelectorAll('select[data-pv2-b2b-model]').forEach((select) => {
  if (select.value) return; // default model set in the editor
  const options = Array.from(select.options).filter((option) => option.value);
  const best = pv2B2bBestModel(select.dataset.productTitle || '', options);
  if (best) select.value = best.value;
});

// Rating (stars + count) → smooth scroll to its anchor (default #vstar-reviews), just under the sticky header.
document.addEventListener('click', (event) => {
  const link = event.target.closest('a.pv2-rating[href^="#"]');
  if (!link) return;
  let target = null;
  try {
    target = document.querySelector(link.getAttribute('href'));
  } catch (error) {
    return;
  }
  if (!target) return;
  event.preventDefault();
  const header = document.querySelector('.header-section');
  const headerHeight = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
  const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
});

// Add-on group V2 (snippet product-v2-addon-check): one add-on category as two stacked choices. Option 2 checks the
// hidden "bundles" input, so the theme's ProductForm adds the add-on with the product. The add-on variant follows the
// product's selection (best option match, e.g. chair Light Gray -> headrest Light Gray). The deal pill opens its
// details on tap (hover / keyboard focus show them through CSS). The ⓘ button opens the theme's drawer by itself
// (aria-controls).
if (!customElements.get('product-addon-check-v2')) {
  class ProductAddonCheckV2 extends HTMLElement {
    connectedCallback() {
      this.bundle = this.querySelector('[data-addon-check]');
      this.opt2 = this.querySelector('[data-addon-opt2]');
      this.dealRow = this.querySelector('[data-addon-check-deal]');
      try {
        this.variants = JSON.parse(this.querySelector('[data-addon-check-variants]').textContent);
      } catch (error) {
        this.variants = [];
      }

      const productInfo = this.closest('product-info');
      this.form = productInfo && document.forms[productInfo.getAttribute('form')];
      this.onVariantChange = (event) => this.follow(event.detail?.variant);
      this.form?.addEventListener('variant:change', this.onVariantChange);

      const selected = productInfo?.querySelector('[data-selected-variant]');
      try {
        this.follow(selected ? JSON.parse(selected.textContent) : null);
      } catch (error) { /* keep the default variant */ }

      this.onClick = this.onClick.bind(this);
      this.onChange = () => this.sync();
      this.onOutside = this.onOutside.bind(this);
      this.onKeydown = (event) => { if (event.key === 'Escape') this.toggleTip(false); };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      this.sync();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('variant:change', this.onVariantChange);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onOutside);
    }

    // Option 2 selected = add-on in the cart
    sync() {
      this.bundle.checked = Boolean(this.opt2?.checked) && !this.opt2.disabled;
    }

    follow(productVariant) {
      if (!productVariant) return;
      this.applyAltImages(productVariant);
      if (this.variants.length < 2) return;
      const best = pv2BestVariant(this.variants, productVariant.options, this.bundle.value);
      if (best && String(best.id) !== this.bundle.value) this.setVariant(best);
    }

    // Second color (e.g. Light Gray): show the option images' second-color versions, here and in the info drawer
    // (the theme moves its drawer to <body> while open, so it is found by id).
    applyAltImages(productVariant) {
      const value = (this.dataset.altValue || '').trim().toLowerCase();
      if (!value) return;
      const alt = (productVariant.options || []).some((option) => String(option).trim().toLowerCase() === value);
      this.classList.toggle('is-alt', alt);
      const drawerId = this.querySelector('.pv2-addon-check__info-button')?.getAttribute('aria-controls');
      document.getElementById(drawerId)?.classList.toggle('is-alt', alt);
    }

    setVariant(variant) {
      this.bundle.value = variant.id;
      this.opt2.value = variant.id;
      this.opt2.disabled = !variant.available;
      if (!variant.available && this.opt2.checked) {
        this.querySelector('[data-addon-opt1]').checked = true;
      }
      this.sync();
      this.querySelector('[data-addon-check-price]').textContent = `+${variant.price}`;
      const regular = this.querySelector('[data-addon-check-regular]');
      regular.textContent = variant.regular;
      regular.hidden = !variant.deal;
      const image = this.querySelector('[data-addon-check-image-wrap] img');
      if (image && variant.image && !this.hasAttribute('data-fixed-image')) {
        image.removeAttribute('srcset');
        image.src = variant.image;
      }
      this.querySelector('[data-addon-check-card]').classList.toggle('has-deal', variant.deal);
      if (!this.dealRow) return;
      this.dealRow.hidden = !variant.deal;
      const fill = (template) => template
        .replaceAll('[price]', variant.price)
        .replaceAll('[regular]', variant.regular)
        .replaceAll('[amount]', variant.amount);
      const label = this.dealRow.querySelector('[data-deal-label]');
      label.textContent = fill(label.dataset.template);
      const tip = this.dealRow.querySelector('[data-deal-tip]');
      if (tip) tip.innerHTML = fill(tip.dataset.template);
    }

    onClick(event) {
      const toggle = event.target.closest('[data-deal-toggle]');
      if (toggle) {
        this.toggleTip(toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      if (event.target.closest('[data-deal-tip], label')) return;
      // Option 2 card padding outside its label (deal row area): select it too
      if (event.target.closest('[data-addon-check-card]') && !this.opt2.disabled) {
        this.opt2.checked = true;
        this.sync();
      }
    }

    toggleTip(open) {
      const toggle = this.querySelector('[data-deal-toggle]');
      if (!toggle) return;
      toggle.setAttribute('aria-expanded', String(open));
      this.dealRow.classList.toggle('is-open', open);
      if (open) document.addEventListener('click', this.onOutside);
      else document.removeEventListener('click', this.onOutside);
    }

    onOutside(event) {
      if (!this.dealRow.contains(event.target)) this.toggleTip(false);
    }
  }

  customElements.define('product-addon-check-v2', ProductAddonCheckV2);
}
